// #1007: the browser read for US fill history (GET /api/polymarket-us/fills).
//
// One bounded, authenticated request through sendUsBrowserRequest (current app
// session, redirect refused, no retry). The decoder is strict and whole-reply:
// one malformed row rejects everything, so the rail shows US as unavailable
// rather than a silently shortened history. Quantities and prices stay exact
// decimal strings; nothing here converts them to numbers.
//
// #1015: like Kalshi and Polymarket, a NO fill shows a price (1 − the venue's YES
// price, marked provisional), and an execution whose stored evidence conflicts is
// shown as a `conflicting` row rather than dropped: each disputed field is null and
// named in `disputed`, and the row belongs in no total.

import { sendUsBrowserRequest } from './browserTradingClient';
import { UsClientError, type UsClientSession } from './tradingClient';

export type UsFillDisputedField = 'attribution' | 'executedAt' | 'price' | 'quantity';
export interface UsFill {
  accountId: string; executionId: string; intentId: string | null;
  marketSlug: string | null; marketTitle: string | null;
  outcome: 'yes' | 'no' | null; action: 'buy' | 'sell' | null;
  quantity: string | null; price: string | null;
  priceBasis: 'yes' | 'provisional' | 'unverified' | 'missing' | 'disputed';
  executedAt: string | null; observedAtMs: number;
  evidenceState: 'consistent' | 'conflicting';
  /** Sorted and unique; empty on every consistent row. */
  disputed: UsFillDisputedField[];
}
export interface UsFillsAccount { accountId: string; status: 'complete' | 'partial' | 'unavailable'; reasons: string[] }
export type UsFillsResult =
  | { ok: true; fills: UsFill[]; accounts: UsFillsAccount[]; complete: boolean; reasons: string[] }
  | { ok: false; code: 'US_FILLS_MALFORMED' | 'US_FILLS_UNAVAILABLE' | 'US_CLIENT_CONTEXT_CHANGED' | 'US_CLIENT_AUTH_REQUIRED' };

const MAX_ROWS = 20;
const ID = /^[A-Za-z0-9_-]{1,256}$/;
const DECIMAL = /^(?:0|[1-9]\d*)(?:\.\d{1,12})?$/;
const ZERO = /^0(?:\.0+)?$/;
const TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/;
const REASON = /^[a-z_]{1,64}$/;
const FILL_KEYS = ['accountId', 'executionId', 'intentId', 'marketSlug', 'marketTitle', 'outcome', 'action',
  'quantity', 'price', 'priceBasis', 'executedAt', 'observedAtMs', 'evidenceState', 'disputed'].sort().join();
const DISPUTED_FIELDS: readonly UsFillDisputedField[] = ['attribution', 'executedAt', 'price', 'quantity'];
const BASES: readonly UsFill['priceBasis'][] = ['yes', 'provisional', 'unverified', 'missing', 'disputed'];

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isId = (v: unknown): v is string => typeof v === 'string' && ID.test(v);
const isReasons = (v: unknown): v is string[] => Array.isArray(v) && v.every(r => typeof r === 'string' && REASON.test(r));

function decodeDisputed(v: unknown): UsFillDisputedField[] | null {
  if (!Array.isArray(v)) return null;
  const out: UsFillDisputedField[] = [];
  for (const f of v) {
    const known = DISPUTED_FIELDS.find(d => d === f);
    // Sorted and unique, so a field is named once and the set has one spelling.
    if (!known || (out.length > 0 && out[out.length - 1] >= known)) return null;
    out.push(known);
  }
  return out;
}

function decodeFill(v: unknown): UsFill | null {
  if (!isRecord(v) || Object.keys(v).sort().join() !== FILL_KEYS) return null;
  const { accountId, executionId, intentId, marketSlug, marketTitle, outcome, action,
    quantity, price, priceBasis, executedAt, observedAtMs, evidenceState } = v;
  const disputed = decodeDisputed(v.disputed);
  if (!disputed || (evidenceState !== 'consistent' && evidenceState !== 'conflicting')) return null;
  const conflicting = evidenceState === 'conflicting';
  // A consistent row names no disputed field; a conflicting one may have none (a peer collision or a shared trade id).
  if (!conflicting && disputed.length > 0) return null;
  const isDisputed = (f: UsFillDisputedField) => disputed.includes(f);
  if (!isId(accountId) || !isId(executionId)) return null;
  // Attribution is null exactly when disputed; the intent may also be null on a conflicting
  // row that spans several of the trader's intents.
  const attributed = !isDisputed('attribution');
  if (!(isId(intentId) || (intentId === null && conflicting))) return null;
  if (attributed ? !isId(marketSlug) : marketSlug !== null) return null;
  if (!(marketTitle === null || (attributed && typeof marketTitle === 'string' && marketTitle.length <= 1000))) return null;
  if (attributed ? (outcome !== 'yes' && outcome !== 'no') || (action !== 'buy' && action !== 'sell')
    : outcome !== null || action !== null) return null;
  if (isDisputed('quantity') ? quantity !== null
    : typeof quantity !== 'string' || !DECIMAL.test(quantity) || ZERO.test(quantity)) return null;
  if (!(price === null || (typeof price === 'string' && DECIMAL.test(price)))) return null;
  const basis = BASES.find(b => b === priceBasis);
  if (!basis || (basis === 'disputed') !== isDisputed('price')) return null;
  // A price is present exactly for a YES-basis YES fill or a provisional (1 − yes) NO fill.
  if ((basis === 'yes' || basis === 'provisional') !== (price !== null)) return null;
  if ((basis === 'yes' && outcome !== 'yes') || (basis === 'provisional' && outcome !== 'no')) return null;
  if (!(executedAt === null || (typeof executedAt === 'string' && TIME.test(executedAt)))) return null;
  if (isDisputed('executedAt') && executedAt !== null) return null;
  if (typeof observedAtMs !== 'number' || !Number.isSafeInteger(observedAtMs) || observedAtMs < 0) return null;
  return { accountId, executionId, intentId: intentId as string | null, marketSlug: marketSlug as string | null,
    marketTitle: marketTitle as string | null, outcome: outcome as UsFill['outcome'], action: action as UsFill['action'],
    quantity: quantity as string | null, price, priceBasis: basis, executedAt, observedAtMs, evidenceState, disputed };
}

export function decodeUsFills(body: unknown): Omit<Extract<UsFillsResult, { ok: true }>, 'ok'> | null {
  if (!isRecord(body) || body.venue !== 'polymarket_us' || body.source !== 'saved_evidence'
    || typeof body.complete !== 'boolean' || !isReasons(body.reasons)
    || !Array.isArray(body.accounts) || !Array.isArray(body.fills) || body.fills.length > MAX_ROWS) return null;
  const accounts: UsFillsAccount[] = [];
  for (const a of body.accounts) {
    if (!isRecord(a) || !isId(a.accountId) || !isReasons(a.reasons)
      || (a.status !== 'complete' && a.status !== 'partial' && a.status !== 'unavailable')) return null;
    accounts.push({ accountId: a.accountId, status: a.status, reasons: a.reasons });
  }
  const fills: UsFill[] = [];
  for (const raw of body.fills) {
    const fill = decodeFill(raw);
    if (!fill) return null;
    fills.push(fill);
  }
  return { fills, accounts, complete: body.complete, reasons: body.reasons };
}

export async function listPolymarketUsFills(limit: number, session: UsClientSession,
  send: typeof sendUsBrowserRequest = sendUsBrowserRequest): Promise<UsFillsResult> {
  let response: Response;
  try {
    response = await send(`/api/polymarket-us/fills?limit=${limit}`, { method: 'GET', cache: 'no-store' }, session);
  } catch (error) {
    if (error instanceof UsClientError
      && (error.code === 'US_CLIENT_CONTEXT_CHANGED' || error.code === 'US_CLIENT_AUTH_REQUIRED')) {
      return { ok: false, code: error.code };
    }
    return { ok: false, code: 'US_FILLS_UNAVAILABLE' };
  }
  if (!response.ok) return { ok: false, code: 'US_FILLS_UNAVAILABLE' };
  let json: unknown;
  try { json = await response.json(); } catch { return { ok: false, code: 'US_FILLS_MALFORMED' }; }
  const decoded = decodeUsFills(json);
  return decoded ? { ok: true, ...decoded } : { ok: false, code: 'US_FILLS_MALFORMED' };
}
